/**
 * SiteConfigure — the Site "Configure" surface (Meraki-style Configure IA).
 *
 * Site-scoped configuration is applied HERE, once, to the whole site — the right
 * granularity for site-wide services (syslog, SNMP, NTP; owner ruling). Each tile
 * is a site action whose Apply fans out across every device in the site through
 * the controller -> owgw, with a per-device audit trail. Device-only settings
 * (e.g. LLDP on one AP's uplink) stay on the device drawer, not here.
 *
 * Requires a site in scope: at org scope we prompt to pick one, since "site
 * configuration" has no meaning org-wide.
 */
import { useSession } from "@/stores/session";
import {
  useSiteConfigActions,
  usePreviewSiteConfigAction,
  useApplySiteConfigAction,
} from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { WanWatchdogCard } from "@/components/site/WanWatchdogCard";
import { DeviceCredentialsCard } from "@/components/site/DeviceCredentialsCard";
import { SiteTabs } from "@/components/site/SiteTabs";
import type { SiteConfigAction, SiteActionExcludedDevice } from "@/types/domain";
import { useState } from "react";

export default function SiteConfigure() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = useSiteConfigActions(siteId);

  // Site config is meaningless without a site chosen.
  if (!siteId) {
    return (
      <div>
        <PageHeader title="Site services" subtitle="Site-wide services, applied to every device in a site" />
        <EmptyState
          title="Select a site to configure"
          hint="Site-wide services (LLDP, and soon syslog/SNMP/NTP) apply to every device in a site. Pick a site from the scope bar above."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Site services"
        subtitle={siteName ? `${siteName} — applies to every device in this site` : undefined}
      />
      <SiteTabs />

      {/* Site device password (switch admin account + Meridian CLI adoption). Rendered outside the
          owgw gate: it works over switch CLI, not owgw config. */}
      <DeviceCredentialsCard siteId={siteId} />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading site configuration…</div>}

      {data && data.owgw_available === false && (
        <EmptyState
          title="No manageable devices yet"
          hint="Site configuration applies through the gateway; no device in this site has a live configuration to change."
        />
      )}

      {data && data.owgw_available && (
        <>
          <p className="mb-3 text-xs text-ink-muted">
            {data.device_count} device{data.device_count === 1 ? "" : "s"} in this site.
          </p>
          <ul className="flex flex-col gap-3">
            {/* Parameterized site services lead — they carry more consequence than
                the boolean action tiles below and must not be scrolled past. */}
            <WanWatchdogCard siteId={siteId} />
            {data.actions.map((action) => (
              <SiteActionCard key={action.id} siteId={siteId} action={action} />
            ))}
          </ul>
          {/* Signposts the IA: parameterized site services land here next. */}
          <p className="mt-4 text-xs text-ink-subtle">
            More site services (syslog, SNMP, NTP) will appear here.
          </p>
        </>
      )}
    </div>
  );
}

/** One site action tile: aggregate state + preview -> confirm -> fan-out apply. */
function SiteActionCard({ siteId, action }: { siteId: string; action: SiteConfigAction }) {
  const preview = usePreviewSiteConfigAction(siteId);
  const apply = useApplySiteConfigAction(siteId);
  const [confirming, setConfirming] = useState(false);

  const previewData = preview.data;
  const applyData = apply.data;

  const start = () => {
    setConfirming(true);
    preview.mutate(action.id);
  };
  const cancel = () => {
    setConfirming(false);
    preview.reset();
  };
  const confirm = () => {
    apply.mutate(action.id, { onSettled: () => setConfirming(false) });
  };

  return (
    <li className="rounded-md border border-line p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">{action.label}</span>
            <StateChip state={action.state} />
          </div>
          <p className="mt-0.5 text-sm text-ink-muted">{action.description}</p>
          <p className="mt-1 text-xs text-ink-subtle">
            {action.applied_count} of {action.supported_count} device
            {action.supported_count === 1 ? "" : "s"} already configured
            {/* Policy skips (switches, HA gateways) are named in the preview. */}
            {action.excluded_devices?.length
              ? ` · ${action.excluded_devices.length} not changed by this action`
              : ""}
          </p>
        </div>

        {/* Apply is org_admin (OP#1580; server: POST /sites/<id>/config/actions/<id>/apply).
            Below that the state is visible, the button is not. */}
        <RoleGate
          min="org_admin"
          mode="hide"
          fallback={<span className="shrink-0 text-xs text-ink-subtle">read-only</span>}
        >
          {!confirming && action.state !== "applied" && (
            <Button variant="secondary" onClick={start}>
              Apply to site
            </Button>
          )}
        </RoleGate>
      </div>

      {/* Apply result (persists after confirming closes). */}
      {applyData && (
        <p className="mt-2 text-xs text-healthy">
          Pushed to {applyData.pushed} device{applyData.pushed === 1 ? "" : "s"}
          {applyData.skipped ? `, ${applyData.skipped} already set` : ""}
          {applyData.errors ? `, ${applyData.errors} failed` : ""}
          {applyData.excluded ? `, ${applyData.excluded} skipped by policy` : ""}.
        </p>
      )}
      {applyData && !confirming && <ExcludedDevices devices={applyData.excluded_devices} />}

      {/* Preview / confirm strip. */}
      {confirming && (
        <div className="mt-3 rounded border border-line bg-surface-2 p-2 text-xs">
          {preview.isPending && <span className="text-ink-muted">Previewing…</span>}
          {preview.isError && <span className="text-critical">Preview failed.</span>}
          {previewData && previewData.changed_count === 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink-muted">
                  {previewData.total_supported === 0
                    ? "Nothing to change — no device in this site takes this action."
                    : `Nothing to change — all ${previewData.total_supported} devices already configured.`}
                </span>
                <Button variant="ghost" onClick={cancel}>
                  Close
                </Button>
              </div>
              <ExcludedDevices devices={previewData.excluded_devices} />
            </div>
          )}
          {previewData && previewData.changed_count > 0 && (
            <div className="flex flex-col gap-2">
              <div>
                <span className="text-ink">
                  Will change {previewData.changed_count} of {previewData.total_supported} device
                  {previewData.total_supported === 1 ? "" : "s"}
                </span>
                <span className="text-ink-muted">
                  {" "}
                  (changes:{" "}
                  <span className="font-mono">
                    {previewData.sample_diff.map((d) => d.path).join(", ") || "configuration"}
                  </span>
                  ) — pushed via the gateway.
                </span>
              </div>
              <ExcludedDevices devices={previewData.excluded_devices} />
              {apply.isError && <span className="text-critical">Apply failed.</span>}
              <div className="flex items-center gap-2">
                <Button variant="primary" disabled={apply.isPending} onClick={confirm}>
                  {apply.isPending ? "Applying…" : "Confirm & apply to site"}
                </Button>
                <Button variant="ghost" disabled={apply.isPending} onClick={cancel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Devices a site action will not touch, each with the controller's plain reason
 * (e.g. "Switch: skipped -- switch configs are never pushed through uCentral").
 * Neutral ink only: a policy skip is not an operational fault, so no status colour.
 */
function ExcludedDevices({ devices }: { devices?: SiteActionExcludedDevice[] }) {
  if (!devices || devices.length === 0) return null;
  return (
    <div className="border-t border-line pt-2">
      <div className="mb-1 text-[0.6875rem] font-medium uppercase tracking-wide text-ink-subtle">
        Not changed ({devices.length})
      </div>
      <ul className="flex flex-col gap-1">
        {devices.map((d) => (
          <li key={d.serial} className="flex flex-wrap gap-x-2">
            <span className="font-mono text-ink">{d.name}</span>
            <span className="text-ink-muted">{d.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Aggregate state chip: applied (all) / partial (some) / none. */
function StateChip({ state }: { state: SiteConfigAction["state"] }) {
  if (state === "applied") {
    return (
      <span className="rounded bg-healthy/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-healthy">
        Applied
      </span>
    );
  }
  if (state === "partial") {
    return (
      <span className="rounded bg-warn/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-warn">
        Partial
      </span>
    );
  }
  return (
    <span className="rounded border border-line px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-ink-subtle">
      Not set
    </span>
  );
}
